import {
    listSessionMetas,
    repairOrphanedChunkSessions,
    type PersistedSessionMeta,
} from "~/hooks/media/durable-audio-recorder-db";
import {
    readPendingCleanupIds,
    retryPendingCleanups,
} from "~/hooks/media/pending-local-cleanup";
import {
    cleanupOldRecordings as cleanupOldLegacyRecordings,
    getAllRecordings as getAllLegacyRecordings,
    type LegacyRecordingData,
} from "~/lib/recording-storage";
import { logger } from "~/lib/logger";

// AIDEV-NOTE: Shared "unfinished recording" detection for the nav pip and the
// cross-page recovery banner. A recording is recoverable when it sits in
// IndexedDB but is NOT plausibly owned by a live recorder in another tab.
// Heartbeat: the durable recorder bumps sessionMeta.updatedAt on every ~1s
// chunk write, so a "recording" row with a fresh updatedAt is an active take.

/** stopped/failed rows younger than this are likely mid-upload on /record-session */
const STOPPED_GRACE_MS = 90 * 1000;
/** "recording" rows with a heartbeat younger than this are an active take in some tab */
const RECORDING_HEARTBEAT_STALE_MS = 2 * 60 * 1000;
/** paused rows don't heartbeat; only surface them after a long quiet period */
const PAUSED_STALE_MS = 60 * 60 * 1000;

export interface RecoverableRecording {
    kind: "durable" | "legacy";
    /** durable: PersistedSessionMeta.id — legacy: localforage key */
    id: string;
    createdAt: string;
    elapsedMs: number;
    approxBytes: number;
    partial: boolean;
    practitionerId?: string | null;
    clientId?: string | null;
    mimeType: string;
    durableMeta?: PersistedSessionMeta;
    legacyData?: LegacyRecordingData;
}

export function getRecoverableRecordingKey(
    recording: Pick<RecoverableRecording, "kind" | "id">,
): string {
    return `${recording.kind}:${recording.id}`;
}

// Exported for unit tests — the heartbeat/staleness rules are the risky part.
export function isDurableSessionRecoverable(
    meta: PersistedSessionMeta,
    now: number,
): boolean {
    if (meta.status === "cancelled") return false;
    if (meta.chunkCount === 0 || meta.totalBytes === 0) return false;

    const updatedAtMs = new Date(meta.updatedAt).getTime();
    const age = Number.isFinite(updatedAtMs)
        ? now - updatedAtMs
        : Number.POSITIVE_INFINITY;

    if (meta.status === "recording") return age > RECORDING_HEARTBEAT_STALE_MS;
    if (meta.status === "paused") return age > PAUSED_STALE_MS;
    // AIDEV-NOTE: "failed" is an explicit terminal state (recorder failure or
    // upload error) — surface it immediately so the nav pip/banner appear the
    // moment an upload dies, not 90s later (Anita QA, Jul 2026).
    if (meta.status === "failed") return true;
    // stopped — allow a short grace window for the in-flight upload
    return age > STOPPED_GRACE_MS;
}

function durableToRecoverable(
    meta: PersistedSessionMeta,
): RecoverableRecording {
    return {
        kind: "durable",
        id: meta.id,
        createdAt: meta.createdAt,
        elapsedMs: meta.elapsedMs,
        approxBytes: meta.totalBytes,
        partial: meta.partial,
        practitionerId: meta.practitionerId ?? null,
        clientId: meta.clientId ?? null,
        mimeType: meta.mimeType,
        durableMeta: meta,
    };
}

function legacyToRecoverable(entry: {
    key: string;
    data: LegacyRecordingData;
}): RecoverableRecording {
    // AIDEV-NOTE: A corrupted createdAt must not throw (toISOString on an
    // invalid Date is a RangeError) — one bad row would otherwise hide every
    // recoverable recording via the outer catch. Fall back to epoch so the
    // entry stays visible and retryable.
    const createdMs = Number(entry.data.createdAt);

    return {
        kind: "legacy",
        id: entry.key,
        createdAt: Number.isFinite(createdMs)
            ? new Date(createdMs).toISOString()
            : new Date(0).toISOString(),
        elapsedMs: entry.data.msDuration,
        approxBytes: Math.round((entry.data.recordDataBase64.length * 3) / 4),
        partial: false,
        practitionerId: entry.data.practitionerId ?? null,
        clientId: entry.data.clientId ?? null,
        mimeType: entry.data.mimeType,
        legacyData: entry.data,
    };
}

export interface ListRecoverableRecordingsOptions {
    /**
     * AIDEV-NOTE: IndexedDB is per-origin, not per-user. Recordings stamped
     * with a recordedByUserId are only surfaced to that user; while the
     * current user is unknown (auth still loading) stamped recordings are
     * hidden rather than risk a cross-account flash on shared browsers.
     * Unstamped recordings (pre-stamping durable rows + all legacy rows)
     * are always surfaced — hiding them would defeat the rescue.
     */
    currentUserId?: string | null;
    /**
     * AIDEV-NOTE: Flag-gated (enable-recording-chunk-recovery, fail-closed).
     * When true, scan the whole IndexedDB chunks store and rebuild sessions-store
     * rows for chunks stranded by the pre-fix index-based read/delete, so the
     * audio becomes listable and re-uploadable. Off by default: this walks every
     * chunk record and is only worth paying for on an affected device.
     */
    repairOrphanedChunks?: boolean;
}

// Exported for unit tests.
export function isVisibleToCurrentUser(
    recordedByUserId: string | null | undefined,
    currentUserId: string | null | undefined,
): boolean {
    if (!recordedByUserId) return true;
    return recordedByUserId === currentUserId;
}

// AIDEV-NOTE: The rescue scan walks EVERY record in the chunks store, so a
// SUCCESSFUL scan runs at most once per page load — listRecoverableRecordings is
// also called on tab focus and visibility change, and re-scanning there would be
// pure waste. The promise is memoized (not just a boolean) so concurrent callers
// share one scan. A FAILED scan is deliberately not cached: openRecorderDb
// rejects outright when another tab blocks a DB upgrade, and caching that would
// disable a user's one chance at rescue until they reloaded the page.
let orphanRepairPromise: Promise<void> | null = null;
let orphanRepairUserId: string | null = null;
// AIDEV-NOTE: Every scan chains onto this, so an account switch mid-scan queues
// the second repair instead of racing it. Two concurrent repairs could otherwise
// rebuild the same missing row and the losing write would attribute a recording
// to the wrong account. Once the first scan lands the row exists, so the second
// takes the reconcile branch and preserves the original recordedByUserId.
let orphanRepairChain: Promise<void> = Promise.resolve();

const ORPHAN_REPAIR_LOCK_NAME = "pc-recording-orphan-chunk-repair";

// AIDEV-NOTE: Cross-tab exclusion, same idiom as RETRY_LOCK_NAME in
// use-recording-retry. The in-module chain only serializes within one tab, and
// IndexedDB is shared across tabs on the origin. Waits for the lock rather than
// using ifAvailable: skipping would silently drop the rescue in the second tab.
// navigator.locks is unavailable on Safari < 15.4, and Safari is exactly the
// population this rescue targets, so fall through to the single-tab chain there.
async function withOrphanRepairLock<T>(task: () => Promise<T>): Promise<T> {
    if (typeof navigator !== "undefined" && navigator.locks?.request) {
        return navigator.locks.request(ORPHAN_REPAIR_LOCK_NAME, task);
    }

    return task();
}

async function runOrphanedChunkRepairOnce(
    recordedByUserId: string,
): Promise<void> {
    // Re-run once if the signed-in user changes, so a rescue is attributed to
    // whoever actually ran it rather than to a stale first caller.
    if (orphanRepairPromise && orphanRepairUserId === recordedByUserId) {
        return orphanRepairPromise;
    }

    orphanRepairUserId = recordedByUserId;
    const previous = orphanRepairChain;
    const attempt = (async () => {
        await previous;
        const repair = await withOrphanRepairLock(() =>
            repairOrphanedChunkSessions(undefined, { recordedByUserId }),
        );
        // AIDEV-NOTE: Log EVERY scan, including scanned=0. This only runs for
        // users in the enable-recording-chunk-recovery segment (a handful of
        // practitioners we are actively rescuing), so the volume is negligible —
        // and a silent zero-result scan is indistinguishable from "the scan never
        // ran", which is precisely the question support needs answered when a
        // practitioner reports seeing no recovery banner.
        logger.info(
            `[RecordingRecovery] Orphaned chunk scan: scanned=${repair.scannedChunks} groups=${repair.groups.length} rebuilt=${repair.repairedSessionIds.length} reconciled=${repair.reconciledSessionIds.length}`,
        );
    })().catch((error: unknown) => {
        // Drop the memo so the next refresh retries instead of requiring a reload.
        if (orphanRepairPromise === attempt) {
            orphanRepairPromise = null;
            orphanRepairUserId = null;
        }
        logger
            .withError(error as Error)
            .warn("Orphaned chunk repair scan failed");
    });

    orphanRepairChain = attempt;
    orphanRepairPromise = attempt;
    return attempt;
}

/**
 * List recordings stranded in local storage (durable IDB + legacy localforage)
 * that a user should be offered to retry. Safe to call from any page; returns
 * [] on any storage error and in non-browser contexts.
 */
export async function listRecoverableRecordings(
    options: ListRecoverableRecordingsOptions = {},
): Promise<RecoverableRecording[]> {
    if (typeof window === "undefined" || !("indexedDB" in window)) {
        return [];
    }

    const now = Date.now();

    try {
        // Legacy recordings have a 7-day retention window (previously enforced
        // on /record-session load only). Enforce it wherever we list.
        await cleanupOldLegacyRecordings().catch(() => 0);

        // AIDEV-NOTE: Skip while auth is still resolving — a rebuilt row stamped
        // with null would stay visible to every account on this browser.
        if (options.repairOrphanedChunks && options.currentUserId) {
            await runOrphanedChunkRepairOnce(options.currentUserId);
        }

        // AIDEV-NOTE: Uploaded recordings whose IDB delete stalled/aborted
        // (iOS WebKit) are already on the server — finish the delete in the
        // background and keep them out of the list so they can't be retried
        // into a duplicate upload. Deliberately NOT awaited: a stalled sweep
        // must not hold up the banner.
        void retryPendingCleanups();
        const pendingCleanupIds = new Set(readPendingCleanupIds());

        const [durableSessions, legacyRecordings] = await Promise.all([
            listSessionMetas().catch(() => [] as PersistedSessionMeta[]),
            getAllLegacyRecordings().catch(
                () => [] as Array<{ key: string; data: LegacyRecordingData }>,
            ),
        ]);

        const recoverable = [
            ...durableSessions
                .filter((meta) => !pendingCleanupIds.has(meta.id))
                .filter((meta) => isDurableSessionRecoverable(meta, now))
                .filter((meta) =>
                    isVisibleToCurrentUser(
                        meta.recordedByUserId,
                        options.currentUserId,
                    ),
                )
                .map(durableToRecoverable),
            ...legacyRecordings.map(legacyToRecoverable),
        ];

        return recoverable.sort(
            (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
        );
    } catch (error) {
        logger
            .withError(error as Error)
            .warn("Failed to list recoverable recordings");
        return [];
    }
}

// AIDEV-NOTE: Cross-component refresh signal. Fired after a retry upload
// succeeds or a recording is deleted so the nav pip + banners resync without
// polling.
const RECOVERABLE_RECORDINGS_CHANGED_EVENT =
    "pc:recoverable-recordings-changed";

export function notifyRecoverableRecordingsChanged(): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event(RECOVERABLE_RECORDINGS_CHANGED_EVENT));
}

export function subscribeToRecoverableRecordingsChanged(
    listener: () => void,
): () => void {
    if (typeof window === "undefined") return () => undefined;
    window.addEventListener(RECOVERABLE_RECORDINGS_CHANGED_EVENT, listener);
    return () =>
        window.removeEventListener(
            RECOVERABLE_RECORDINGS_CHANGED_EVENT,
            listener,
        );
}
